// ./src/utils/api/PersonGroupService.ts
//
// PersonGroup (household / family / company) service — the groups a contact can
// belong to. Used by the contact editor's group picker (incl. create-in-place).
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// A group's kind — validated server-side against the same set.
export const PERSON_GROUP_KIND_VALUES = ["household", "family", "company", "organization"] as const;
export type PersonGroupKind = (typeof PERSON_GROUP_KIND_VALUES)[number];

const PERSON_GROUP_FIELDS = `
  id
  accountId
  name
  kind
  description
  status
  rights
  createdAt
  updatedAt
`;

const PERSON_GROUP_LIST_QUERY = gql`
  query PersonGroupList($filter: PersonGroupFilter, $params: PaginationAndSorting) {
    personGroupList(filter: $filter, params: $params) {
      code
      data { ${PERSON_GROUP_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PERSON_GROUP_GET_QUERY = gql`
  query PersonGroupGet($id: ID) {
    personGroupGet(id: $id) {
      code
      data { ${PERSON_GROUP_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PERSON_GROUP_CREATE_MUTATION = gql`
  mutation PersonGroupCreate($params: PersonGroupInput) {
    personGroupCreate(params: $params) {
      code
      data { ${PERSON_GROUP_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PERSON_GROUP_UPDATE_MUTATION = gql`
  mutation PersonGroupUpdate($where: PersonGroupWhereInput, $params: PersonGroupInput) {
    personGroupUpdate(where: $where, params: $params) {
      code
      data { ${PERSON_GROUP_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PERSON_GROUP_REMOVE_MUTATION = gql`
  mutation PersonGroupRemove($where: PersonGroupWhereInput) {
    personGroupRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

export interface PersonGroup {
  id?: string;
  accountId?: string;
  name?: string | null;
  kind?: string | null;
  description?: string | null;
  status?: number;
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PersonGroupInput {
  name?: string | null;
  kind?: string | null;
  description?: string | null;
}

export interface PersonGroupFilter {
  id?: string[] | null;
  kind?: string[] | null;
  searchKeyword?: string | null;
}

export interface PersonGroupWhereInput {
  id?: string;
}

export class PersonGroupService {
  static async list(
    filter?: PersonGroupFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(PERSON_GROUP_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(PERSON_GROUP_GET_QUERY, { id });
  }

  static async create(params: PersonGroupInput): Promise<OpResult> {
    return ApiService.executeMutation(PERSON_GROUP_CREATE_MUTATION, { params });
  }

  static async update(where: PersonGroupWhereInput, params: PersonGroupInput): Promise<OpResult> {
    return ApiService.executeMutation(PERSON_GROUP_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(PERSON_GROUP_REMOVE_MUTATION, { where: { id } });
  }
}
